import { Component, EventEmitter, Input, Output, SimpleChanges, computed, signal } from '@angular/core';
import { Form, FormBuilder, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_RANGE_SELECTION_STRATEGY, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import moment from 'moment';
import { CustomRangeStrategy } from './custom-range.strategy';

@Component({
  selector: 'app-date-range',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './date-range.component.html',
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: CustomRangeStrategy
    }
  ]
})
export class DateRangeComponent {
  @Input() maxRange: number | null = null;
  @Input() label: string = 'Rango de fechas';
  @Input() clearTrigger: any;
  @Input() reverseDateFormat: boolean = false;
  @Input() dateWithTime: boolean = false;
  @Input() dateWithTimeAllDay: boolean = false;
  @Input() shouldEmitRange: boolean = false;
  @Output() rangeChange = new EventEmitter<any>();
  @Input() errorMessage: string | null = null;

  fromDateSignal = signal<Date | null>(null);
  form: FormGroup;

  @Input() blockUntilToday = false;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      fromDate: [null],
      toDate: [null]
    });
  }

  ngOnInit() {

    this.form.get('fromDate')!.valueChanges.subscribe(value => {
      this.fromDateSignal.set(value || null);
    });

    this.form.valueChanges.subscribe(value => {
      const { fromDate, toDate } = value;

      if (!fromDate || !toDate) {
        return;
      }

      const diffDays =
        Math.abs(
          (new Date(toDate).getTime() -
            new Date(fromDate).getTime()) /
          86400000
        );

      if (this.maxRange != null && diffDays > this.maxRange) {
        this.form.get('toDate')?.reset(null, { emitEvent: false });
        return;
      }

      this.onChangeDateRange({
        fromDate,
        toDate
      });
    });

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clearTrigger'] && !changes['clearTrigger'].firstChange) {
      this.clear();
    }
  }

  onChangeDateRange(event: any): void {
    const { fromDate, toDate } = event ?? {};
    if (!fromDate || !toDate) {
      return;
    }

    const format = this.reverseDateFormat
      ? (this.dateWithTime ? 'YYYY-MM-DD hh:mm:ss' : 'YYYY-MM-DD')
      : (this.dateWithTime ? 'DD-MM-YYYY hh:mm:ss' : 'DD-MM-YYYY');

    const from = this.dateWithTimeAllDay
      ? moment(fromDate)
        .startOf('day')
        .format(this.reverseDateFormat ? 'YYYY-MM-DD HH:mm:ss' : 'DD-MM-YYYY HH:mm:ss')
      : moment(event.fromDate).format(format);

    const to = this.dateWithTimeAllDay
      ? moment(toDate)
        .endOf('day')
        .format(this.reverseDateFormat ? 'YYYY-MM-DD HH:mm:ss' : 'DD-MM-YYYY HH:mm:ss')
      : moment(event.toDate).format(format);

    const value = this.shouldEmitRange
      ? { from, to }
      : `${from}:${to}`;

    this.rangeChange.emit(value);
  }

  clear() {
    this.form.reset(
      {
        fromDate: null,
        toDate: null
      },
      { emitEvent: false }
    );

    this.fromDateSignal.set(null);
  }

  dateFilterFn = computed(() => {
    const from = this.fromDateSignal();
    const maxRange = this.maxRange;

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return (date: Date | null) => {
      if (!date) return false;

      const current = date.getTime();

      // bloquear fechas futuras
      if (this.blockUntilToday && current > today.getTime()) {
        return false;
      }

      if (!from || maxRange == null) {
        return true;
      }

      // rango simétrico (permite adelante y atrás)
      const diffDays =
        Math.abs(current - from.getTime()) / 86400000;

      return diffDays <= maxRange;
    };
  });
}